/**
 * Pre-Action View for Poker Reborn
 *
 * Container managing pre-action checkboxes (radio-group behavior).
 * Shows while hero is waiting (not their turn) to queue an action.
 *
 * @see docs/screens/room/ACTION_PANEL.md - Pre-Action Checkboxes section
 */

import { Container } from 'pixi.js';
import { PreActionCheckbox } from './PreActionCheckbox';
import type { PreActionType, PreActionSelection } from '@/types/game';

// =============================================================================
// STYLES
// =============================================================================

const STYLES = {
  button: {
    width: 100,
    height: 55,
    spacing: 15,
  },
};

// =============================================================================
// PRE-ACTION VIEW
// =============================================================================

export class PreActionView extends Container {
  private screenWidth: number;
  private checkboxes: PreActionCheckbox[] = [];
  private selectedPreAction: PreActionSelection | null = null;

  /** Callback when selection changes */
  onSelectionChanged: ((selection: PreActionSelection | null) => void) | null = null;

  constructor(screenWidth: number) {
    super();
    this.screenWidth = screenWidth;
    this.visible = false;
  }

  /** Show checkboxes for "no bet" scenario: Check, Check/Fold, All In */
  showCheckScenario(): void {
    this.clearCheckboxes();

    this.addCheckbox('check');
    this.addCheckbox('checkFold');
    this.addCheckbox('allin');

    this.restoreSelection();
    this.layoutCheckboxes();
    this.visible = true;
  }

  /** Show checkboxes for "facing bet" scenario: Fold, Call $X, Call Any, All In */
  showBetScenario(callAmount: number): void {
    this.clearCheckboxes();

    this.addCheckbox('fold');

    const callCheckbox = this.addCheckbox('callAmount');
    callCheckbox.setAmount(callAmount);

    this.addCheckbox('callAny');
    this.addCheckbox('allin');

    this.restoreSelection();
    this.layoutCheckboxes();
    this.visible = true;
  }

  /** Get the current selection */
  getSelection(): PreActionSelection | null {
    return this.selectedPreAction;
  }

  /** Clear the current selection (deselect all) */
  clearSelection(): void {
    this.selectedPreAction = null;
    for (const checkbox of this.checkboxes) {
      checkbox.checked = false;
    }
  }

  /** Invalidate "Call $X" if selected (call amount changed) */
  invalidateCallAmount(): void {
    if (this.selectedPreAction?.type === 'callAmount') {
      this.selectedPreAction = null;
      for (const checkbox of this.checkboxes) {
        if (checkbox.preActionType === 'callAmount') {
          checkbox.checked = false;
        }
      }
      this.onSelectionChanged?.(null);
    }
  }

  /** Hide and clear all */
  hide(): void {
    this.visible = false;
  }

  /** Resize for screen width */
  resize(screenWidth: number): void {
    this.screenWidth = screenWidth;
    if (this.visible) {
      this.layoutCheckboxes();
    }
  }

  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  private addCheckbox(type: PreActionType): PreActionCheckbox {
    const checkbox = new PreActionCheckbox(type, {
      width: STYLES.button.width,
      height: STYLES.button.height,
    });

    checkbox.onClick = () => { this.handleCheckboxClick(checkbox); };
    this.checkboxes.push(checkbox);
    this.addChild(checkbox);
    return checkbox;
  }

  private handleCheckboxClick(clicked: PreActionCheckbox): void {
    if (clicked.checked) {
      // Toggle off — deselect
      clicked.checked = false;
      this.selectedPreAction = null;
    } else {
      // Deselect all others, select this one
      for (const checkbox of this.checkboxes) {
        checkbox.checked = false;
      }
      clicked.checked = true;
      this.selectedPreAction = {
        type: clicked.preActionType,
        amount: undefined,
      };
    }
    this.onSelectionChanged?.(this.selectedPreAction);
  }

  /** Restore visual check state if the selected pre-action matches a visible checkbox */
  private restoreSelection(): void {
    if (!this.selectedPreAction) return;

    for (const checkbox of this.checkboxes) {
      if (checkbox.preActionType === this.selectedPreAction.type) {
        checkbox.checked = true;
        return;
      }
    }

    // Selected type not in current scenario — clear it
    this.selectedPreAction = null;
    this.onSelectionChanged?.(null);
  }

  private clearCheckboxes(): void {
    for (const checkbox of this.checkboxes) {
      this.removeChild(checkbox);
      checkbox.destroy();
    }
    this.checkboxes = [];
  }

  private layoutCheckboxes(): void {
    const buttonWidth = STYLES.button.width;
    const spacing = STYLES.button.spacing;
    const totalWidth = this.checkboxes.length * buttonWidth + (this.checkboxes.length - 1) * spacing;
    const startX = (this.screenWidth - totalWidth) / 2 + buttonWidth / 2;

    for (let i = 0; i < this.checkboxes.length; i++) {
      const checkbox = this.checkboxes[i]!;
      checkbox.x = startX + i * (buttonWidth + spacing);
      checkbox.y = 0;
    }
  }
}
